import type { Trace } from '@/features/traceability/type/Tool';
import type { ToolAggregation, CategoryAggregation, ReferenceAggregation } from './trace';
import type { MetricKey } from '../type/dynamicChart';
import { shortToolId } from './format';

export function getXMetricValue(
  data: Trace | (ToolAggregation & { toolId: string }) | ReferenceAggregation,
  metric: MetricKey
): string | null {
  switch (metric) {
    case 'toolId':
      return 'toolId' in data ? data.toolId : null;
    case 'traceId':
      if ('traceId' in data && data.traceId) {
        return String(data.traceId);
      }
      return null;
    case 'reference':
      return 'reference' in data ? data.reference : null;
    default:
      return null;
  }
}

// Helper to extract metric value from trace or aggregation
export function getMetricValue(
  data:
    | (Trace & { toolId: string })
    | (ToolAggregation & { toolId: string })
    | CategoryAggregation
    | ReferenceAggregation,
  metric: MetricKey
): number | null {
  switch (metric) {
    case 'totalMachiningDuration':
      return data.totalMachiningDuration;
    case 'pieceMachiningDuration':
      return data.pieceMachiningDuration;
    case 'machinedVolume':
      return data.machinedVolume;
    case 'machinedDistance':
      return data.machinedDistance;
    case 'machinedPieces':
      return data.machinedPieces;
    case 'malround':
      return data.malround;
    default:
      return null;
  }
}

// Helper to get category value from data based on colorBy
export function getCategoryValue(
  data:
    | (Trace & { toolId: string })
    | (ToolAggregation & { toolId: string })
    | ReferenceAggregation,
  colorBy: 'toolId' | 'machine' | 'material' | 'reference' | 'toolState' | undefined
): string {
  if (!colorBy) return 'default';

  switch (colorBy) {
    case 'toolId':
      return 'toolId' in data ? shortToolId(data.toolId) : 'unknown';
    case 'machine': {
      // Try to get .machine.name, then .machine, then undefined
      if ('machine' in data) {
        const m = (data as { machine?: { name?: string } | string | undefined }).machine;
        if (typeof m === 'object' && m && 'name' in m) {
          return m.name || 'unknown';
        }
        if (typeof m === 'string') {
          return m;
        }
      }
      return 'unknown';
    }
    case 'material': {
      if ('material' in data) {
        const m = (data as { material?: { name?: string } | string | undefined }).material;
        if (typeof m === 'object' && m && 'name' in m) {
          return m.name || 'unknown';
        }
        if (typeof m === 'string') {
          return m;
        }
      }
      return 'unknown';
    }
    case 'reference':
      return 'reference' in data ? data.reference : 'unknown';
    case 'toolState': {
      if ('toolState' in data && data.toolState && typeof data.toolState === 'object') {
        return (data.toolState as { toolState?: string }).toolState ?? 'unknown';
      }
      return 'unknown';
    }
    default:
      return 'default';
  }
}

/** X metrics whose values are categories (one bar / slice per value) rather than numbers. */
export function isCategoricalXAxis(
  metric: string
): metric is 'machine' | 'material' | 'toolHolder' | 'lubrication' {
  return ['machine', 'material', 'toolHolder', 'lubrication'].includes(metric);
}
